import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PortalV2Layout from './PortalV2Layout';
import {
  Search, RefreshCw, AlertCircle, FileText, MapPin, Calendar, ChevronRight,
  Archive, DollarSign, ChevronLeft, X,
} from 'lucide-react';

const ITEMS_PER_PAGE = 25;

const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const ClientV2Jobs = () => {
  const { authFetch } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [archivedFilter, setArchivedFilter] = useState('all');
  const [stats, setStats] = useState({ total: 0, served: 0, active: 0, closed: 0, canceled: 0 });
  const [currentPage, setCurrentPage] = useState(1);

  const [unpaidFilter, setUnpaidFilter] = useState(searchParams.get('filter') === 'unpaid');
  const [unpaidJobNumbers, setUnpaidJobNumbers] = useState(new Set());

  const fetchUnpaid = async () => {
    try {
      const r = await authFetch('/api/portal/invoices');
      if (r.ok) {
        const d = await r.json();
        setUnpaidJobNumbers(new Set((d.invoices || []).filter(i => parseFloat(i.balance_due || 0) > 0).map(i => i.servemanager_job_number)));
      }
    } catch {}
  };

  useEffect(() => {
    if (searchParams.get('filter') === 'unpaid') { setUnpaidFilter(true); fetchUnpaid(); }
    // eslint-disable-next-line
  }, [searchParams]);

  const clearUnpaid = () => {
    setUnpaidFilter(false);
    const np = new URLSearchParams(searchParams); np.delete('filter');
    setSearchParams(np, { replace: true });
  };

  const fetchAll = async () => {
    try {
      const r = await authFetch('/api/portal/jobs');
      if (!r.ok) return;
      const d = await r.json();
      const all = d.jobs || [];
      const served = all.filter(j => j.service_status === 'Served');
      const active = all.filter(j => j.service_status !== 'Served' && !j.archived_at && !j.client_canceled);
      const closed = all.filter(j => j.service_status !== 'Served' && j.archived_at && j.attempt_count > 0 && !j.client_canceled);
      const canceled = all.filter(j => (j.service_status !== 'Served' && j.archived_at && (!j.attempt_count || j.attempt_count === 0)) || j.client_canceled);
      setStats({ total: all.length, served: served.length, active: active.length, closed: closed.length, canceled: canceled.length });
    } catch {}
  };

  const fetchJobs = async () => {
    setLoading(true); setError(null);
    try {
      let url = '/api/portal/jobs';
      if (statusFilter !== 'all') url += `?status=${statusFilter}`;
      const r = await authFetch(url);
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed to fetch jobs');
      setJobs((await r.json()).jobs || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { fetchJobs(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => { setCurrentPage(1); }, [statusFilter, searchTerm, archivedFilter]);

  const handleStatClick = (f) => {
    setStatusFilter(f);
    if (f === 'closed' || f === 'canceled') setArchivedFilter('archived');
    else if (f === 'active') setArchivedFilter('active');
    else setArchivedFilter('all');
    setCurrentPage(1);
  };

  const getEffectiveStatus = (job) => {
    if (!job) return 'In Progress';
    const hasAttempts = (job.attempts && job.attempts.length > 0) || (job.attempt_count && job.attempt_count > 0);
    const lastAttempt = job.attempts?.length > 0 ? job.attempts[job.attempts.length - 1] : null;
    const lastType = lastAttempt ? (lastAttempt.serve_type || lastAttempt.service_status || '') : '';
    if (job.client_canceled) return 'Canceled';
    if (job.archived_at && !hasAttempts && job.service_status !== 'Served') return 'Canceled';
    if (lastType.toLowerCase().includes('bad address')) return 'Bad Address';
    if (lastType.toLowerCase().includes('non-service') || job.service_status === 'Non-Service') return 'Non-Service';
    return job.service_status || (job.archived_at ? 'Closed' : 'In Progress');
  };

  const statusBadgeStyle = (job) => {
    const s = getEffectiveStatus(job);
    const c = ({ Served: 'var(--pv2-green-text)', Attempted: 'var(--pv2-amber-text)', 'Non-Service': 'var(--pv2-red-bright)', 'Bad Address': '#fb923c', Canceled: 'var(--pv2-text-subtle)' }[s] || '#60a5fa');
    return { color: c };
  };

  const filteredJobs = jobs.filter(job => {
    if (unpaidFilter && !unpaidJobNumbers.has(job.servemanager_job_number)) return false;
    if (statusFilter === 'all') {
      if (archivedFilter === 'active' && job.archived_at) return false;
      if (archivedFilter === 'archived' && !job.archived_at) return false;
    }
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (job.recipient_name || '').toLowerCase().includes(q)
      || (job.case_number || '').toLowerCase().includes(q)
      || (job.case_title || '').toLowerCase().includes(q)
      || (job.servemanager_job_number || '').toLowerCase().includes(q)
      || (job.address?.city || '').toLowerCase().includes(q);
  });

  const totalPages = Math.ceil(filteredJobs.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedJobs = filteredJobs.slice(startIndex, endIndex);

  const StatCard = ({ filter, label, value, meta, color }) => {
    const active = statusFilter === filter;
    return (
      <button onClick={() => handleStatClick(filter)} className="pv2-stat" data-testid={`pv2-filter-${filter}`}
        style={{ textAlign: 'left', cursor: 'pointer', border: active ? `1px solid ${color}` : undefined, background: active ? `${color}11` : undefined }}>
        <div className="pv2-stat-label" style={{ color: active ? color : undefined }}>{label}</div>
        <div className="pv2-stat-value" style={{ color: active ? color : undefined }}>{value}</div>
        <div className="pv2-stat-meta">{meta}</div>
      </button>
    );
  };

  const Pagination = ({ testIdPrefix }) => totalPages > 1 ? (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', border: '1px solid var(--pv2-ink-line)', background: 'var(--pv2-ink)', flexWrap: 'wrap', gap: 12 }}>
      <span style={{ fontSize: 12, color: 'var(--pv2-text-muted)' }}>
        Showing <strong style={{ color: 'var(--pv2-text)' }}>{startIndex + 1}-{Math.min(endIndex, filteredJobs.length)}</strong> of <strong style={{ color: 'var(--pv2-text)' }}>{filteredJobs.length}</strong> jobs
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="pv2-btn pv2-btn-ghost" style={{ padding: 8 }} data-testid={`${testIdPrefix}-prev`}>
          <ChevronLeft size={14} />
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1).map((p, idx, arr) => {
          const showDots = idx > 0 && p - arr[idx - 1] > 1;
          return (
            <React.Fragment key={p}>
              {showDots && <span style={{ color: 'var(--pv2-text-subtle)', padding: '0 4px' }}>…</span>}
              <button onClick={() => setCurrentPage(p)} className={`pv2-btn ${currentPage === p ? 'pv2-btn-primary' : 'pv2-btn-ghost'}`} style={{ minWidth: 36, padding: '8px 10px' }} data-testid={`${testIdPrefix}-page-${p}`}>
                {p}
              </button>
            </React.Fragment>
          );
        })}
        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="pv2-btn pv2-btn-ghost" style={{ padding: 8 }} data-testid={`${testIdPrefix}-next`}>
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  ) : null;

  return (
    <PortalV2Layout
      eyebrow="Operations"
      title={unpaidFilter ? 'Jobs with Unpaid Invoices' : 'My Jobs'}
      subtitle={unpaidFilter ? 'Click on a job to view invoice details and pay' : 'Track your process serving jobs'}
      crumbs={<strong>My Jobs</strong>}
      action={
        <button onClick={fetchJobs} disabled={loading} className="pv2-btn pv2-btn-ghost" data-testid="pv2-refresh-jobs">
          <RefreshCw size={14} strokeWidth={1.5} className={loading ? 'pv2-skeleton' : ''} /> Refresh
        </button>
      }
    >
      {unpaidFilter && (
        <div className="pv2-alert pv2-alert-amber" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <DollarSign size={14} strokeWidth={1.5} />
            <span><strong>Showing Jobs with Unpaid Invoices</strong> — {filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''} with outstanding balance</span>
          </div>
          <button onClick={clearUnpaid} className="pv2-btn pv2-btn-ghost" style={{ padding: '6px 10px' }}><X size={12} /> Show All Jobs</button>
        </div>
      )}

      <section className="pv2-stats-grid pv2-stats-grid--5" style={{ marginBottom: 24 }}>
        <StatCard filter="all" label="Total Jobs" value={stats.total} meta="ALL SUBMITTED" color="var(--pv2-red-bright)" />
        <StatCard filter="Served" label="Served" value={stats.served} meta="SUCCESSFULLY COMPLETED" color="var(--pv2-green-text)" />
        <StatCard filter="active" label="Active" value={stats.active} meta="IN PROGRESS" color="#60a5fa" />
        <StatCard filter="closed" label="Non Served" value={stats.closed} meta="UNABLE TO COMPLETE" color="var(--pv2-red-bright)" />
        <StatCard filter="canceled" label="Canceled" value={stats.canceled} meta="PRIOR TO ATTEMPTS" color="var(--pv2-amber-text)" />
      </section>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 280 }}>
          <Search size={14} strokeWidth={1.5} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--pv2-text-subtle)' }} />
          <input className="pv2-input" placeholder="Search by recipient, case number, city…" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: 36 }} data-testid="pv2-jobs-search" />
        </div>
        {statusFilter === 'all' && (
          <div style={{ display: 'flex', border: '1px solid var(--pv2-ink-line)', overflow: 'hidden', flexShrink: 0 }}>
            {['active', 'all', 'archived'].map(opt => {
              const active = archivedFilter === opt;
              return (
                <button key={opt} onClick={() => setArchivedFilter(opt)}
                  style={{ padding: '10px 16px', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, border: 0, background: 'transparent', color: active ? 'var(--pv2-red-bright)' : 'var(--pv2-text-muted)', fontWeight: active ? 600 : 400, cursor: 'pointer' }}
                  data-testid={`pv2-archived-${opt}`}>
                  {opt === 'archived' ? 'Archived' : opt === 'active' ? 'Active' : 'All'}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ marginBottom: 16 }}><Pagination testIdPrefix="pv2-pag-top" /></div>

      {error && <div className="pv2-alert pv2-alert-red"><AlertCircle size={14} /><span>{error}</span></div>}

      <div className="pv2-card" style={{ overflow: 'hidden' }}>
        {loading ? <div style={{ padding: 48, textAlign: 'center', color: 'var(--pv2-text-muted)' }}>Loading…</div>
          : filteredJobs.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center' }}>
              <FileText size={28} style={{ opacity: 0.3, marginBottom: 12 }} />
              <div style={{ fontSize: 14, color: 'var(--pv2-text-muted)' }}>{searchTerm ? 'No jobs match your search' : 'No jobs found'}</div>
            </div>
          ) : (
            <div>
              {paginatedJobs.map(job => (
                <Link key={job.id} to={`/portal/jobs/${job.servemanager_job_id || job.id}`}
                  data-testid={`pv2-job-${job.id}`}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '16px 20px', borderBottom: '1px solid var(--pv2-ink-line-soft)', textDecoration: 'none', color: 'inherit', opacity: job.archived_at ? 0.7 : 1, ...(job.is_rush ? { background: 'rgba(248, 113, 113, 0.07)', boxShadow: 'inset 3px 0 0 0 var(--pv2-red-bright)' } : {}) }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--pv2-text)' }}>{job.recipient_name || 'Unknown Recipient'}</span>
                      <span className="pv2-pill" style={statusBadgeStyle(job)}>{getEffectiveStatus(job)}</span>
                      {job.archived_at && <span className="pv2-pill" style={{ color: 'var(--pv2-text-muted)' }}><Archive size={9} strokeWidth={1.5} /> Archived</span>}
                    </div>
                    {(job.case_title || job.case_number) && (
                      <div style={{ fontSize: 12, color: 'var(--pv2-text-subtle)', marginTop: 4, fontStyle: 'italic' }}>
                        {job.case_title || job.case_number}
                      </div>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 6, fontSize: 11, color: 'var(--pv2-text-muted)' }}>
                      {job.address?.city && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                          <MapPin size={10} strokeWidth={1.5} />
                          {job.address.city}, {job.address.state}{job.address.postal_code ? ` ${job.address.postal_code}` : (job.address.zip ? ` ${job.address.zip}` : '')}
                        </span>
                      )}
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><Calendar size={10} strokeWidth={1.5} /> {fmtDate(job.created_at)}</span>
                      {job.servemanager_job_number && <span style={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--pv2-text-subtle)' }}>#{job.servemanager_job_number}</span>}
                    </div>
                    {job.attempt_count > 0 && (
                      <div style={{ fontSize: 11, color: 'var(--pv2-amber-text)', marginTop: 6, fontWeight: 500 }}>
                        {job.attempt_count} attempt{job.attempt_count > 1 ? 's' : ''} made
                      </div>
                    )}
                  </div>
                  <ChevronRight size={14} color="var(--pv2-text-subtle)" style={{ marginTop: 8 }} />
                </Link>
              ))}
            </div>
          )}
      </div>

      <div style={{ marginTop: 16 }}><Pagination testIdPrefix="pv2-pag-bot" /></div>
    </PortalV2Layout>
  );
};

export default ClientV2Jobs;
